import type { PspRules } from "@CommonTypes/backend/PspRules";

/** The weight rules the app seeds, in integer grams; 68039 g = 150 lb. */
export const DEFAULT_WEIGHT_RULES: NonNullable<PspRules["weight_rules"]> = {
  default_shelf_weight_limit: 68039,
  heavy_rule: null,
  resolve_overweight_shelves: false,
  swap_heavy_brands_below_max_height: false,
  swap_fill_floor_pct: 0.9,
  report: false,
};

/**
 * The rules a new merchflow starts with: what the backend compares against to
 * tell an untouched switch from the merchant's. The vertical-blocking fields
 * hold the frontend's `verticalBlockingPreset` values; keep the two in step.
 */
export const defaultPspRules: PspRules = {
  fixture: {
    can_adjust_shelf_notches: true,
    can_add_or_delete_shelves: false,
    shelves_same_height_as_base_pog: true,
    vertical_overlap: false,
    merged_shelf_width_tolerance_pct: 10,
    vertical_split_section: 1,
    finger_space: 0,
  },
  range: {
    can_add_non_core_range_products: true,
    can_apply_squeeze_to_placed_products: false,
    balance_facings_mode: "EMPTY_SPACE_ONLY",
    adjust_facings_mode: "EXACT_MATCH",
  },
  merchandising: {
    vertical_blocking_mode: true,
    can_break_cases_trays_if_core_range_not_fit: true,
    can_fill_extra_space_next_to_cases_trays_with_loose_products: false,
    how_should_loose_products_be_placed_with_respect_to_their_cases_trays: "BEHIND",
    leverage_alternative_orientations: true,
    stack_to_shelf_top: false,
    stack_past_max_stack: false,
    extra_facing_marginal_value: 0.5,
    snaking: false,
    cdt_stacking: false,
    items_squeeze_option: "none",
  },
  high_density_mode: {
    enabled: true,
    snaking_strategy: "none",
    variant_contiguous: true,
    place_all_variants: true,
    allow_breaking_variant_sequencing: false,
    vertical_blocking_mode: true,
    strict_rectangles: true,
  },
  high_density_building_blocks_mode: {
    enabled: false,
    direction: "BACKWARD",
    start_from: { type: "BIGGEST" },
  },
  mudmap_level: "CDT1",
  optimize_on: "SALES",
  relax_factors: {
    space_mix: 0,
    either_or: 1,
    both_or_none: 1,
    prereq: "inf",
    shelf_item_count: 1,
    fixed_notch_num: 0.5,
    finger_space: 5,
    units_not_enough_dos: 0.1,
    units_not_enough_mos: 0.2,
    units_too_many: 0,
    facings_not_enough: 0,
    facings_too_many: 50,
    min_width: 0.5,
    min_distribution: 100,
    width_slack: 0.1,
    cdt1_contiguous: 50,
    cdt2_contiguous: 50,
    cdt3_contiguous: 50,
    cdt1_order: 50,
    cdt2_order: 50,
    cdt3_order: 50,
    item_id_order: 50,
    item_span: 50,
    cdt1_span: 0,
    cdt2_span: 0,
    cdt3_span: 0.2,
    secondary_orientation: 0.3,
    rows_not_enough: 10,
    shelf_segment_space: 100,
  },
  mudmap_generation_penalty_multiplier: {
    totalSpaceSlack: 100,
    cdt0SectionsSpanning: 10,
    cdt0ShelvesSpanning: 10,
    cdt2SectionsSpanning: 1,
    cdt2ShelvesSpanning: 1,
    cdt0RoundingSlack: 20,
    cdt2RoundingSlack: 20,
    cdt1BreakingSequencing: 100,
    cdt2BreakingSequencing: 100,
    cdtSpaceMixSlack: 100,
    cdt0ShelvesMisaligned: 40,
    itemWidthSatisfied: 1,
    cdt0ShelfSpaceMisalignment: 100,
    cdt2ShelfSpaceMisalignment: 100,
    verticalBlockShelfPresence: 100,
    cdt0CellXMisalignment: 100,
    cdt2CellXMisalignment: 100,
  },
  weight_rules: DEFAULT_WEIGHT_RULES,
};
